import { reportText, reportSiteGroupKey } from './reportSiteIdentity';
import type { SiteRagReport } from '../types/siteRag';

export type ReportPickRow = {
  id: string;
  siteId?: string;
  siteName: string;
  version: number;
  minorVersion: number;
  reportType: string;
  parentReportId?: string;
  templateId?: string;
  templateVersionUsed?: number;
};

export function formatReportPickLabel(
  row: Pick<ReportPickRow, 'id' | 'siteName' | 'version' | 'minorVersion' | 'reportType'>,
): string {
  const ver = row.version ?? 1;
  const minor = row.minorVersion ?? 0;
  const type = row.reportType ?? 'feasibility';
  const suffix = row.id.length > 6 ? row.id.slice(-6) : row.id;
  return `v${ver}.${minor} · ${type} · ${reportText(row.siteName) || row.id} · …${suffix}`;
}

function sortReportsNewestFirst(
  a: Pick<LineageReport, 'version' | 'createdAt'>,
  b: Pick<LineageReport, 'version' | 'createdAt'>,
): number {
  const verDiff = finiteNumber(b.version, 1) - finiteNumber(a.version, 1);
  if (verDiff !== 0) return verDiff;
  return reportDate(b.createdAt) - reportDate(a.createdAt);
}

type LineageReport = {
  id: string;
  // Firestore list metadata can predate these fields or contain invalid values.
  siteId?: unknown;
  siteName?: unknown;
  version?: number;
  createdAt?: string;
  parentReportId?: string;
};

function finiteNumber(value: unknown, fallback = 0): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback;
}

function reportDate(value: unknown): number {
  const timestamp = Date.parse(reportText(value));
  return Number.isFinite(timestamp) ? timestamp : 0;
}

function reportGroupKey(report: LineageReport): string {
  return reportSiteGroupKey(report);
}

/**
 * Prefer explicit site identity; only legacy rows fall back to their name.
 * Unnamed, unscoped metadata stays isolated. A malformed parent chain cannot
 * join different explicit sites, including through an unscoped intermediate.
 * Original records remain untouched so display fallbacks never become evidence.
 */
export function groupReportsByLineage<T extends LineageReport>(reports: readonly T[]): T[][] {
  const siteGroups = new Map<string, T[]>();
  for (const r of reports) {
    const key = reportGroupKey(r);
    if (!siteGroups.has(key)) siteGroups.set(key, []);
    siteGroups.get(key)!.push(r);
  }

  const idToReport = new Map(reports.map(report => [report.id, report]));
  const links = new Map<string, Set<string>>();
  for (const r of reports) {
    const parent = idToReport.get(reportText(r.parentReportId));
    if (!parent) continue;
    const parentKey = reportGroupKey(parent);
    const childKey = reportGroupKey(r);
    if (parentKey === childKey) continue;
    for (const [from, to] of [[parentKey, childKey], [childKey, parentKey]] as const) {
      if (!links.has(from)) links.set(from, new Set());
      links.get(from)!.add(to);
    }
  }

  const groups: T[][] = [];
  const visited = new Set<string>();
  for (const key of siteGroups.keys()) {
    if (visited.has(key)) continue;
    const pending = [key];
    const component: string[] = [];
    while (pending.length) {
      const next = pending.pop()!;
      if (visited.has(next)) continue;
      visited.add(next);
      component.push(next);
      pending.push(...(links.get(next) ?? []));
    }
    const identifiedSites = component.filter(groupKey => groupKey.startsWith('site:'));
    if (identifiedSites.length > 1) {
      // Conflicting lineage is not evidence that either site's reports moved.
      groups.push(...component.map(groupKey => siteGroups.get(groupKey)!));
    } else {
      groups.push(component.flatMap(groupKey => siteGroups.get(groupKey)!));
    }
  }
  return groups;
}

/** Select library rows even while bodies are still loading from object storage. */
export function selectReportLibraryReports<T extends LineageReport & { address?: unknown; overallScore?: number }>(
  reports: readonly T[],
  search: string,
  sort: 'date' | 'score' | 'name' | 'readiness',
): { filteredReports: T[]; versionGroups: Map<string, T[]> } {
  const versionGroups = new Map<string, T[]>();
  for (const versions of groupReportsByLineage(reports)) {
    const sorted = [...versions].sort(sortReportsNewestFirst);
    if (sorted[0]) versionGroups.set(sorted[0].id, sorted);
  }
  const query = search.toLowerCase();
  const filteredReports = [...versionGroups.values()].map(versions => versions[0]!).filter(report =>
    !query || reportText(report.siteName).toLowerCase().includes(query)
      || reportText(report.address).toLowerCase().includes(query),
  );
  filteredReports.sort((a, b) => sort === 'score'
    ? finiteNumber(b.overallScore) - finiteNumber(a.overallScore)
    : sort === 'name'
      ? reportText(a.siteName).localeCompare(reportText(b.siteName))
      : reportDate(b.createdAt) - reportDate(a.createdAt));
  return { filteredReports, versionGroups };
}

/** Keep only the newest report from each lineage chain. */
export function pickLatestReportsPerLineage<T extends ReportPickRow>(
  rows: T[],
  reportsById?: Map<string, SiteRagReport>,
): T[] {
  if (rows.length <= 1) return rows;
  const fullReports: LineageReport[] = rows.map(r => {
    const fromStore = reportsById?.get(r.id);
    if (fromStore) {
      return {
        id: fromStore.id,
        siteId: fromStore.siteId,
        siteName: fromStore.siteName ?? r.siteName,
        version: fromStore.version ?? r.version,
        createdAt: fromStore.createdAt,
        parentReportId: fromStore.parentReportId,
      };
    }
    return {
      id: r.id,
      siteId: r.siteId,
      siteName: r.siteName,
      version: r.version,
      createdAt: undefined,
      parentReportId: r.parentReportId,
    };
  });
  const groups = groupReportsByLineage(fullReports);
  const latestIds = new Set<string>();
  for (const versions of groups) {
    const sorted = [...versions].sort(sortReportsNewestFirst);
    if (sorted[0]) latestIds.add(sorted[0].id);
  }
  return rows.filter(r => latestIds.has(r.id));
}

export function toReportPickRow(r: SiteRagReport): ReportPickRow {
  return {
    id: r.id,
    siteId: reportText(r.siteId) || undefined,
    siteName: reportText(r.siteName),
    version: r.version ?? 1,
    minorVersion: r.minorVersion ?? 0,
    reportType: r.reportType ?? 'feasibility',
    parentReportId: r.parentReportId,
    templateId: (r as { templateId?: string }).templateId,
    templateVersionUsed: (r as { templateVersionUsed?: number }).templateVersionUsed,
  };
}
